import React, { useMemo, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    IconButton,
    Button,
    Chip,
    InputAdornment,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const BACKEND_URL = process.env.REACT_APP_BACKEND;

export default function WorkshopSelector({
    title,
    timeslot,
    workshops,
    selectedWorkshop,
    favourites = [],
    onSelect,
    onToggleFavourite,
    onOpenInfo,
    isWorkshopUnavailable,
}) {
    const [search, setSearch] = useState('');

    const filteredWorkshops = useMemo(() => {
        const searchTerm = search.trim().toLowerCase();

        let result = workshops;

        if (searchTerm) {
            result = result.filter((workshop) =>
                workshop.name?.toLowerCase().includes(searchTerm)
            );
        }

        // Favourites first, then normal workshops.
        return [...result].sort((a, b) => {
            const aFavourite = favourites.includes(a.documentId);
            const bFavourite = favourites.includes(b.documentId);

            if (aFavourite && !bFavourite) return -1;
            if (!aFavourite && bFavourite) return 1;

            return (a.name ?? '').localeCompare(b.name ?? '');
        });
    }, [workshops, favourites, search]);

    const favouriteWorkshops = filteredWorkshops.filter((workshop) =>
        favourites.includes(workshop.documentId)
    );

    const normalWorkshops = filteredWorkshops.filter(
        (workshop) => !favourites.includes(workshop.documentId)
    );

    const renderWorkshopCard = (workshop) => {
        const unavailable = isWorkshopUnavailable(
            workshop.documentId,
            timeslot
        );

        const selected =
            selectedWorkshop === workshop.documentId;

        const favourite =
            favourites.includes(workshop.documentId);

        const availableSpots =
            workshop.availableSpots?.[timeslot] ?? 0;

        const sessionSpots =
            (workshop.session_spots ?? 0) *
            (workshop.sessionCount?.[timeslot] ?? 0);

        return (
            <Card
                key={workshop.documentId}
                sx={{
                    position: 'relative',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',

                    border: '2px solid',
                    borderColor: selected
                        ? 'primary.main'
                        : 'divider',

                    borderRadius: 3,

                    opacity: unavailable ? 0.55 : 1,

                    transition: 'all 0.15s ease',

                    '&:hover': {
                        transform: unavailable
                            ? 'none'
                            : 'translateY(-2px)',
                        boxShadow: unavailable
                            ? undefined
                            : 3,
                    },
                }}
            >
                {/* Favourite button */}
                <IconButton
                    aria-label={
                        favourite
                            ? 'Remove from favourites'
                            : 'Add to favourites'
                    }
                    onClick={() =>
                        onToggleFavourite?.(workshop.documentId)
                    }
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 2,
                        backgroundColor: 'background.paper',

                        '&:hover': {
                            backgroundColor: 'background.paper',
                        },
                    }}
                >
                    {favourite ? (
                        <FavoriteIcon color="error" />
                    ) : (
                        <FavoriteBorderIcon />
                    )}
                </IconButton>

                {/* Workshop image */}
                {workshop.image && (
                    <Box
                        component="img"
                        src={
                            workshop.image.url ??
                            "https://placehold.co/600x400"
                        }
                        alt=""
                        sx={{
                            width: '100%',
                            height: 150,
                            objectFit: 'cover',
                        }}
                    />
                )}

                <CardContent
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        gap: 1,
                    }}
                >
                    <Typography
                        variant="h6"
                        component="h3"
                        sx={{
                            fontWeight: 600,
                            pr: 4,
                        }}
                    >
                        {workshop.name}
                    </Typography>

                    {/* Availability */}
                    <Box>
                        {unavailable ? (
                            <Chip
                                size="small"
                                label="Pole saadaval"
                                color="error"
                            />
                        ) : (
                            <Chip
                                size="small"
                                label={`${availableSpots} kohta`}
                                color={
                                    availableSpots <= 3
                                        ? 'warning'
                                        : 'default'
                                }
                            />
                        )}
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            mt: 'auto',
                            pt: 2,
                        }}
                    >
                        {/* Info */}
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<InfoOutlinedIcon />}
                            onClick={() =>
                                onOpenInfo(workshop.documentId)
                            }
                            sx={{
                                flex: 1,
                            }}
                        >
                            Lähemalt
                        </Button>

                        {/* Select */}
                        <Button
                            variant={
                                selected
                                    ? 'contained'
                                    : 'outlined'
                            }
                            size="small"
                            disabled={unavailable}
                            startIcon={
                                selected
                                    ? <CheckCircleIcon />
                                    : null
                            }
                            onClick={() =>
                                onSelect(workshop.documentId)
                            }
                            sx={{
                                flex: 1,
                            }}
                        >
                            {selected ? 'Valitud' : 'Vali'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        );
    };


    return (
        <Box>
            <Typography
                variant="h6"
                component="h2"
                sx={{
                    mb: 1.5,
                    fontWeight: 600,
                }}
            >
                {title}
            </Typography>

            {/* Search */}
            <TextField
                fullWidth
                size="small"
                placeholder="Otsi töötuba..."
                value={search}
                onChange={(event) =>
                    setSearch(event.target.value)
                }
                sx={{ mb: 2 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                }}
            />

            {/* Favourites */}
            {favouriteWorkshops.length > 0 && !search && (
                <Box sx={{ mb: 3 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                        }}
                    >
                        <FavoriteIcon
                            color="error"
                            fontSize="small"
                        />

                        <Typography
                            variant="subtitle1"
                            fontWeight={600}
                        >
                            Sinu lemmikud
                        </Typography>
                    </Box>

                    {/* Horizontal swipe area */}
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            overflowX: 'auto',
                            pb: 1,
                            px: 0.5,

                            scrollSnapType: 'x mandatory',

                            '& > *': {
                                scrollSnapAlign: 'start',
                                flex: {
                                    xs: '0 0 85%',
                                    sm: '0 0 45%',
                                    md: '0 0 32%',
                                },
                            },

                            '&::-webkit-scrollbar': {
                                height: 6,
                            },
                        }}
                    >
                        {favouriteWorkshops.map(
                            renderWorkshopCard
                        )}
                    </Box>
                </Box>
            )}

            {/* All workshops */}
            <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ mb: 1 }}
            >
                Kõik töötoad
            </Typography>

            {normalWorkshops.length === 0 &&
            favouriteWorkshops.length === 0 ? (
                <Typography
                    color="text.secondary"
                    sx={{ py: 3, textAlign: 'center' }}
                >
                    Sobivaid töötubasid ei leitud.
                </Typography>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(2, 1fr)',
                            md: 'repeat(3, 1fr)',
                        },
                        gap: 2,
                    }}
                >
                    {normalWorkshops.map(
                        renderWorkshopCard
                    )}
                </Box>
            )}
        </Box>
    );
}